import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Eye, EyeOff, BookOpen } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1515948725-edac7b5bb0fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHx1cHNjYWxlJTIwcmVzdGF1cmFudCUyMGludGVyaW9yJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzc5NzI2NzkzfDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenido, ${user.name || user.email}`);
      const dest = user.role === "owner" ? "/owner" : user.role === "admin" ? "/admin" : "/waiter";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error de autenticación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#fcfbfd]" data-testid="login-page">
      {/* Hero image side */}
      <div className="relative hidden md:block md:w-1/2 lg:w-3/5">
        <img src={HERO} alt="restaurante" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#3e166c]/65 mix-blend-multiply" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center p-2">
              <Logo size={44} variant="plain" />
            </div>
            <span className="font-display text-2xl tracking-tight font-bold">oRdeN</span>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#b182ec] mb-4">Punto de Venta</p>
            <h1 className="font-display text-5xl lg:text-6xl font-black leading-[1.05] mb-6">
              Toma pedidos.<br/>Cierra mesas.<br/>Sin fricción.
            </h1>
            <p className="text-white/80 max-w-md leading-relaxed">
              La app que tus meseros van a amar usar. Diseñada para ser rápida en el piso, clara en la caja, y completa en los reportes.
            </p>
          </div>
          <p className="text-xs text-white/60">© oRdeN — Toma de pedidos para restaurantes</p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md animate-fade-up" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-10">
            <Logo size={48} variant="plain" />
            <span className="font-display text-2xl font-bold text-[#1a0f2e]">oRdeN</span>
          </div>

          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-3">Iniciar sesión</p>
          <h2 className="font-display text-4xl font-black text-[#1a0f2e] mb-2 tracking-tight">Bienvenido</h2>
          <p className="text-[#7248ae] mb-10">Entra con tu correo y contraseña.</p>

          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Correo</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                required
                autoComplete="email"
                data-testid="login-email-input"
                className="h-12 border-[#b182ec]/50 focus:border-[#3e166c] focus:ring-2 focus:ring-[#b182ec]/30 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  className="h-12 border-[#b182ec]/50 focus:border-[#3e166c] focus:ring-2 focus:ring-[#b182ec]/30 rounded-xl pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7248ae] hover:text-[#3e166c]"
                  data-testid="toggle-password-visibility"
                  aria-label="Mostrar contraseña"
                >
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="mt-3 flex justify-end">
                <Link to="/forgot-password" className="text-sm text-[#3e166c] hover:underline" data-testid="forgot-password-link">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 rounded-xl bg-[#3e166c] hover:bg-[#7248ae] text-white font-semibold tracking-wide transition-all active:scale-[0.98]"
            >
              {loading ? "Entrando…" : (<><LogIn className="mr-2 h-5 w-5" />Entrar</>)}
            </Button>
          </div>

          <div className="mt-12 p-4 rounded-xl bg-[#3e166c]/5 border border-[#b182ec]/30">
            <p className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 font-semibold">Cuentas demo</p>
            <ul className="text-sm text-[#1a0f2e] space-y-1">
              <li><strong>Admin activo</strong>: admin1@orden.mx / Admin123</li>
              <li><strong>Admin vencido</strong>: admin3@orden.mx / Admin123</li>
              <li><strong>Admin vencido 2</strong>: admin4@orden.mx / Admin123</li>
              <li><strong>Mesero</strong>: mesero1@orden.mx / Mesero123</li>
            </ul>
          </div>

          <Link
            to="/manual"
            data-testid="login-manual-link"
            className="mt-6 inline-flex items-center justify-center gap-2 w-full h-11 rounded-xl border border-[#b182ec]/50 text-[#3e166c] hover:bg-[#3e166c]/5 hover:border-[#3e166c] transition-colors text-sm font-medium"
          >
            <BookOpen className="h-4 w-4" />
            Ver manual de uso
          </Link>
        </form>
      </div>
    </div>
  );
}
